// ============================================
// services/auth.ts
//
// 登录态管理.
//
// 职责:
//   - 包装 callApi, 处理 token 过期 / 缺失 → 自动跳登录页
//   - 提供 currentUser 缓存 (避免每次进页面都拉 my-profile)
//   - 登出: 清 token + 清缓存
//
// 跟 services/api.ts 的关系:
//   api.ts 是低层 fetch 封装, 不感知"登录失败要跳哪"
//   auth.ts 在 api.ts 之上, 加业务策略
//
// FromApp.tsx 不走 auth 包装 (它本来就是建 session 的入口, 不能在建之前要登录)
// ============================================

import { callApi, getToken, setToken, clearToken } from './api';
import type { ApiResult } from './api';
import type { UserInfo } from '../types/community';

const USER_CACHE_KEY = 'COMMUNITY_USER_INFO_CACHE';

// ─────────────────────────────────────────────
// 当前用户缓存
// ─────────────────────────────────────────────

export function getCachedUser(): UserInfo | null {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) as UserInfo : null;
  } catch {
    return null;
  }
}

export function setCachedUser(u: UserInfo): void {
  try {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
  } catch {
    // ignore quota
  }
}

export function clearCachedUser(): void {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // ignore
  }
}

// ─────────────────────────────────────────────
// 是否已登录 (轻量判断, 不验证 token 有效性)
// ─────────────────────────────────────────────

export function isLoggedIn(): boolean {
  return !!getToken();
}

// ─────────────────────────────────────────────
// 登出
// ─────────────────────────────────────────────

export function logout(): void {
  clearToken();
  clearCachedUser();
}

// ─────────────────────────────────────────────
// 包装版 callApi:401 / 鉴权失败 → 自动登出 + 提示重新登录
//
// 用法:
//   const r = await authedCallApi('community-publish-project', { ... });
//   if (!r.ok && r.redirected_to_login) return; // 已被处理
// ─────────────────────────────────────────────

export interface AuthedApiResult<T = unknown> extends ApiResult<T> {
  /** 是否因鉴权失败被本函数主动登出 */
  redirected_to_login?: boolean;
}

const AUTH_ERROR_CODES = new Set([
  'NO_TOKEN',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'AUTH_FAILED',
]);

export async function authedCallApi<T = unknown>(
  action: string,
  body: Record<string, unknown> = {},
): Promise<AuthedApiResult<T>> {
  if (!getToken()) {
    // 未登录直接返,不发请求
    return {
      ok: false,
      error: 'NO_TOKEN',
      message: '请先登录',
      redirected_to_login: true,
    };
  }

  const r = await callApi<T>(action, body) as AuthedApiResult<T>;

  if (!r.ok && r.error && AUTH_ERROR_CODES.has(String(r.error))) {
    // 登录失效 → 清状态
    logout();
    r.redirected_to_login = true;
    // 不主动 window.location 跳页:让调用方决定 UI 怎么处理
    // (例:在按钮 onClick 里弹个 toast "请重新登录" 而不是强跳)
  }

  return r;
}

// ─────────────────────────────────────────────
// 刷新当前用户信息 (从 my-profile 拉, 写入缓存)
// Week 2 接入 community-get-my-profile 时可用.
// ─────────────────────────────────────────────

export async function refreshCurrentUser(): Promise<UserInfo | null> {
  if (!isLoggedIn()) return null;

  const r = await authedCallApi<{ profile: UserInfo }>('community-get-my-profile');
  if (r.ok && r.profile) {
    setCachedUser(r.profile as UserInfo);
    return r.profile as UserInfo;
  }
  return null;
}

// ─────────────────────────────────────────────
// 设置初始登录态 (FromApp.tsx 在 bootstrap-session 成功后调)
// ─────────────────────────────────────────────

export function setInitialSession(token: string, userInfo: UserInfo): void {
  setToken(token);
  setCachedUser(userInfo);
}
